import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, XCircle, Quote, Ban } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLearnerStories() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasonById, setReasonById] = useState({});
  const [saving, setSaving] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/learner-stories/admin/queue?status=${status}`, { withCredentials: true });
      setItems(data.items || []);
    } catch {
      toast.error("Couldn't load stories queue.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [status]);

  const decide = async (id, decision) => {
    const reason = (reasonById[id] || "").trim();
    if (decision === "reject" && !reason) {
      toast.error("Add a short reason before rejecting.");
      return;
    }
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      await axios.post(
        `${API}/api/learner-stories/admin/decide/${id}`,
        { decision, reason: reason || null },
        { withCredentials: true }
      );
      toast.success(decision === "approve" ? "Published." : "Rejected.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save decision.");
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="admin-stories-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Learner story moderation</h1>
            <p className="text-[11px] text-slate-500">Only approved quotes appear publicly. Keep it fair & factual.</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-3">
        <div className="flex gap-1 mb-3">
          {[
            ["pending", "Pending"],
            ["approved", "Approved"],
            ["rejected", "Rejected"],
          ].map(([k, l]) => (
            <button
              key={k}
              data-testid={`stories-filter-${k}`}
              onClick={() => setStatus(k)}
              className={`px-2.5 py-1.5 text-[11px] font-bold rounded-full border ${
                status === k
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700"
              }`}
            >{l}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div data-testid="stories-empty" className="rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-200 dark:border-slate-700 p-6 text-center">
            <Quote className="w-5 h-5 mx-auto text-slate-400 mb-1" />
            <p className="text-xs text-slate-500 font-bold">Nothing in this list</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((s) => (
              <li key={s.id} data-testid={`story-item-${s.id}`} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
                <blockquote className="text-sm text-slate-800 dark:text-slate-100 leading-snug">"{s.quote}"</blockquote>
                <p className="mt-2 text-[11px] text-slate-500">
                  — {s.is_anonymous ? "Anonymous" : (s.display_name || "Anonymous")}
                  <span className="ml-2 text-slate-400">(learner: {s.learner_name} · for {s.instructor_name})</span>
                </p>
                <p className="text-[10px] text-slate-400 mt-1">Submitted {s.created_at?.slice(0, 10)}</p>
                {s.rejection_reason && (
                  <p className="mt-2 text-[11px] font-bold text-amber-700 dark:text-amber-300">Rejected: {s.rejection_reason}</p>
                )}
                {status === "pending" && (
                  <div className="mt-3 space-y-2">
                    <input
                      data-testid={`stories-reason-${s.id}`}
                      type="text"
                      placeholder="Reason (required when rejecting)"
                      value={reasonById[s.id] || ""}
                      onChange={(e) => setReasonById((r) => ({ ...r, [s.id]: e.target.value }))}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        data-testid={`stories-reject-${s.id}`}
                        onClick={() => decide(s.id, "reject")}
                        disabled={saving[s.id]}
                        className="px-3 py-2 text-xs font-extrabold rounded-xl bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-60 inline-flex items-center justify-center gap-1"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                      <button
                        data-testid={`stories-approve-${s.id}`}
                        onClick={() => decide(s.id, "approve")}
                        disabled={saving[s.id]}
                        className="px-3 py-2 text-xs font-extrabold rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60 inline-flex items-center justify-center gap-1"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                    </div>
                  </div>
                )}
                {status === "approved" && (
                  <button
                    data-testid={`stories-unpublish-${s.id}`}
                    onClick={() => decide(s.id, "reject")}
                    className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 dark:text-amber-300"
                  >
                    <Ban className="w-3.5 h-3.5" /> Unpublish
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
